import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, Trash2, Upload, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";

const readB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});
const prettySize = (b) => (b == null ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

export default function SourceDocuments({ rfqId, reloadKey = 0 }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/rfqs/${rfqId}/source-files`).then((r) => setFiles(r.data)).catch(() => {});
  }, [rfqId]);
  useEffect(() => { load(); }, [load, reloadKey]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await readB64(file);
      await api.post(`/rfqs/${rfqId}/source-files`, { filename: file.name, content_type: file.type, file_base64: b64 });
      toast.success("Source document saved");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/rfqs/${rfqId}/source-files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove ${f.filename}?`)) return;
    await api.delete(`/rfqs/${rfqId}/source-files/${f.id}`); load(); toast.success("Removed");
  };

  return (
    <div className="rounded-lg border border-border bg-white p-5" data-testid="source-documents">
      <div className="mb-4 flex items-center gap-2">
        <Paperclip className="h-4 w-4 text-primary" />
        <h2 className="font-heading text-base font-bold tracking-tight">RFQ Issued To Somcore</h2>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">The original RFQ document(s) received from the client — kept here for reference.</p>

      <div className="space-y-2">
        {files.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid={`source-file-${f.id}`}>
            <button onClick={() => download(f)} className="flex min-w-0 items-center gap-2 text-left" data-testid={`open-source-${f.id}`}>
              <FileText className="h-4 w-4 shrink-0 text-[#1B4383]" />
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-slate-800">{f.filename}</span>
                <span className="block text-xs text-muted-foreground">{prettySize(f.size)} · {f.uploaded_by_name || "—"}</span>
              </span>
            </button>
            <div className="flex shrink-0 items-center gap-2">
              <button onClick={() => download(f)} className="text-slate-400 hover:text-[#1B4383]" data-testid={`download-source-${f.id}`}><Download className="h-4 w-4" /></button>
              <button onClick={() => remove(f)} className="text-slate-400 hover:text-destructive" data-testid={`delete-source-${f.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {!files.length && <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground" data-testid="source-empty">No source document saved yet.</p>}
      </div>

      <label className="mt-3 block">
        <input type="file" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="source-upload-input" />
        <Button asChild variant="outline" size="sm" className="w-full" disabled={uploading}>
          <span>{uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Upload Document</span>
        </Button>
      </label>
    </div>
  );
}
